import { useEffect, useState } from "react";
import { Volume2, VolumeX, Keyboard } from "lucide-react";
import {
  isKeyboardSoundEnabled, setKeyboardSoundEnabled,
  getSwitchProfile, setSwitchProfile, SWITCH_PROFILES, playKeyClick,
} from "@/lib/keyboardSound";

/**
 * Pill toggle for typing-click sound + switch-profile picker (Blue/Brown/Red).
 * Persists both to localStorage. Clicking a profile also plays a preview
 * click so the student can audition the sound without having to type.
 */
export default function KeyboardSoundToggle({ className = "" }) {
  const [on, setOn] = useState(false);
  const [profile, setProfile] = useState("blue");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOn(isKeyboardSoundEnabled());
    setProfile(getSwitchProfile());
    const syncOn = (e) => setOn(!!e.detail);
    const syncSwitch = (e) => setProfile(e.detail || "blue");
    window.addEventListener("stb-keyboard-sound-change", syncOn);
    window.addEventListener("stb-kb-switch-change", syncSwitch);
    return () => {
      window.removeEventListener("stb-keyboard-sound-change", syncOn);
      window.removeEventListener("stb-kb-switch-change", syncSwitch);
    };
  }, []);

  const toggle = () => {
    const next = !on;
    setKeyboardSoundEnabled(next);
    setOn(next);
    if (next) playKeyClick(0, profile);   // audible confirmation
  };

  const pickProfile = (id) => {
    setSwitchProfile(id);
    setProfile(id);
    if (on) playKeyClick(0, id);         // preview
  };

  return (
    <div className={`inline-flex items-stretch gap-1 ${className}`} data-testid="keyboard-sound-wrap">
      <button
        type="button"
        onClick={toggle}
        data-testid="keyboard-sound-toggle"
        aria-pressed={on}
        aria-label={on ? "Turn keyboard sound off" : "Turn keyboard sound on"}
        className={`inline-flex items-center gap-2 px-3 h-9 rounded-l-lg border text-[11px] uppercase tracking-widest font-bold transition-colors ${
          on
            ? "border-brand-gold bg-brand-gold text-background"
            : "border-border bg-secondary/40 text-muted-foreground hover:border-brand-gold/40 hover:text-foreground"
        }`}
      >
        {on ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
        {on ? "Sound on" : "Sound off"}
      </button>
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid="keyboard-switch-picker"
          aria-haspopup="menu"
          aria-expanded={open}
          disabled={!on}
          className={`inline-flex items-center gap-2 px-3 h-9 rounded-r-lg border-y border-r text-[11px] uppercase tracking-widest font-bold transition-colors ${
            on
              ? "border-brand-gold text-brand-gold hover:bg-brand-gold/10"
              : "border-border text-muted-foreground/50 cursor-not-allowed"
          }`}
        >
          <Keyboard className="w-3.5 h-3.5" />
          {SWITCH_PROFILES.find((p) => p.id === profile)?.label.replace("Cherry MX ", "") || "Blue"}
        </button>
        {open && on && (
          <div
            className="absolute right-0 top-10 z-30 w-64 border border-border bg-background rounded-lg shadow-xl overflow-hidden"
            data-testid="keyboard-switch-menu"
          >
            {SWITCH_PROFILES.map((p) => {
              const active = p.id === profile;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => { pickProfile(p.id); setOpen(false); }}
                  data-testid={`keyboard-switch-option-${p.id}`}
                  className={`w-full text-left px-3 py-2.5 border-b border-border last:border-b-0 transition-colors ${
                    active ? "bg-brand-gold/10" : "hover:bg-secondary/60"
                  }`}
                >
                  <div className={`text-[11px] uppercase tracking-widest font-bold ${active ? "text-brand-gold" : "text-foreground"}`}>
                    {p.label}
                  </div>
                  <div className="text-[10px] font-mono text-muted-foreground mt-0.5">{p.desc}</div>
                </button>
              );
            })}
            <div className="text-[9px] font-mono text-muted-foreground px-3 py-2 bg-secondary/60">
              Click a profile to preview. Cherry MX Blue is loudest — best for exam-hall practice; Red is quiet enough for late-night sessions.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
